import React, { useState } from 'react';
import { FiUploadCloud, FiFile, FiX } from 'react-icons/fi';

/**
 * VideoUploadProgress Component
 * Modern video upload with drag-and-drop and real-time progress tracking
 */
const VideoUploadProgress = ({ 
  onFileSelect, 
  onUpload, 
  isUploading = false,
  uploadProgress = 0,
  uploadedBytes = 0,
  totalBytes = 0,
  title = '',
  onTitleChange,
  rowNumber = 1,
  onRowNumberChange
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'video/mp4') {
        setSelectedFile(file);
        onFileSelect && onFileSelect(file);
      } else {
        alert('Please select an MP4 video file');
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'video/mp4') {
        setSelectedFile(file);
        onFileSelect && onFileSelect(file);
      } else {
        alert('Please select an MP4 video file');
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect && onFileSelect(null);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="add-video-form">
      {/* Title Input */}
      <div className="form-group">
        <label className="form-label">Video Title</label>
        <input
          type="text"
          className="form-input"
          placeholder="Enter video title..."
          value={title}
          onChange={(e) => onTitleChange && onTitleChange(e.target.value)}
          disabled={isUploading}
        />
      </div>

      {/* Row Number Input */}
      <div className="form-group">
        <label className="form-label">Order Number</label>
        <input
          type="number"
          className="form-input"
          placeholder="Enter order number..."
          value={rowNumber}
          onChange={(e) => onRowNumberChange && onRowNumberChange(parseInt(e.target.value) || 1)}
          min="1"
          disabled={isUploading}
        />
      </div>

      {/* Drag & Drop Upload Zone */}
      <div
        className={`upload-zone ${dragOver ? 'dragover' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={isUploading}
        />
        
        {!selectedFile ? (
          <>
            <div className="upload-icon">
              <FiUploadCloud />
            </div>
            <div className="upload-text">
              {dragOver ? 'Drop video here' : 'Drag & drop video here'}
            </div>
            <div className="upload-hint">
              or click to browse (MP4 only)
            </div>
          </>
        ) : (
          <div className="upload-file-info">
            <div className="file-icon">
              <FiFile />
            </div>
            <div className="file-details">
              <div className="file-name">{selectedFile.name}</div>
              <div className="file-size">{formatBytes(selectedFile.size)}</div>
            </div>
            {!isUploading && (
              <button
                type="button"
                className="icon-btn delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
              >
                <FiX />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {isUploading && (
        <div className="upload-progress">
          <div className="progress-header">
            <span className="progress-title">Uploading video...</span>
            <span className="progress-percentage">{uploadProgress}%</span>
          </div>
          
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>

          <div className="progress-info">
            <span>{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}</span>
            <span>
              {uploadProgress < 100 ? 'Uploading...' : 'Processing...'}
            </span>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && !isUploading && (
        <button
          type="button"
          className="btn-modern btn-primary"
          onClick={onUpload}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          <FiUploadCloud />
          Upload Video
        </button>
      )}
    </div>
  );
};

export default VideoUploadProgress;
